import { SUMMARY_DATA_REQUEST, CALLSPAGE_QUICKFILTER_DATA_REQUEST, CALLTYPE_DATA_REQUEST, CALLMONTH_DATA_REQUEST, CALLS_SUMMARY_DETAILS_CANCEL, CALLS_CALLMONTH_DETAILS_CANCEL, CALLS_CALLTYPE_DETAILS_CANCEL, CALLS_GEO_TIN_COUNT_DATA_REQUEST} from "@src/middleware/actions/actionTypes";
import { getSummaryData, getQuickFiltersData, getCallTypeData, getCallMonthData, getCallsGeoTinCountData } from "@src/middleware/services/service-interaction/call-page.service";
import { summaryDataLoading, fetchSummaryDataSuccess, getQuickFilterDataSuccess, callTypeDataLoading, fetchCallTypeDataSuccess, callMonthDataLoading, fetchCallMonthDataSuccess,
  callsPagePollingStarted, callsPagePollingCompleted, callsPagePollingFailed,
  callsPagePollingReset,
  fetchCallsGeoTinCountDataLoading,
  fetchCallsGeoTinCountDataSuccess
 } from "@src/middleware/actions/service-interaction/callPageActions";
import { isHighVolumeTinFlow } from "@src/utility/createStorage"
import { startJobPolling } from "@src/utility/CommonBulkPoll";

import {
  all,
  call,
  cancelled,
  put,
  race,
  take,
  takeLatest,
} from 'redux-saga/effects';

const isContractBulkFlow = () =>
  sessionStorage.getItem('contractIdNavigation') === 'true' &&
  sessionStorage.getItem('hiVolumeContractFlow') === 'true';

export function* fetchSummaryData(action: any) {
  // Get geoCriteria from action payload (passed from Main.tsx)
  // Geo flow uses the normal API; only high-volume/contract flows poll bulk jobs.
  const geoCriteria = action?.geoCriteria;
  const { payload } = action;
  const key = isHighVolumeTinFlow() || isContractBulkFlow();
  let stopPollingFn: (() => void) | undefined;


  yield put(callsPagePollingReset()); // Reset polling counter at the start of each summary data fetch

  try {
    if (key) {
      yield put(callsPagePollingStarted()); // Increment polling counter only for bulk flow
    }
    yield put(summaryDataLoading(true));

    // Call getSummaryData with geoCriteria (with race for cancellation support).
    const { result: response, cancel } = yield race({
      result: call(getSummaryData, payload || action?.payload, geoCriteria),
      cancel: take(CALLS_SUMMARY_DETAILS_CANCEL),
    });

    if (cancel) {
      yield put(summaryDataLoading(false));
      if (key) {
        yield put(callsPagePollingCompleted()); // Decrement polling counter on cancel (bulk flow only)
      }
      return;
    }

    // Check the actual transport flow. Geo criteria alone does not mean polling.
    if (key && response?.Data?.JobId && (
      response?.Data?.Status === "ACCEPTED"
    )) {
      // ============================================================
      // POLLING FLOW - Response contains jobId for polling
      // ============================================================

      // Step 2: Extract jobId from job submission response
      const jobId = response?.Data?.JobId;
      const isJobSubmitted = response?.Data?.Status === "ACCEPTED" ||
                            response?.Data?.Message === "Job submitted successfully";

      if (jobId && isJobSubmitted) {
        // Step 3: Start polling with the jobId - Poll GET /job-status/result/{jobId}
        let fullPollingResponse: any = null;
        const pollingPromise = () => {
          return new Promise((resolve, reject) => {
            startJobPolling(
              jobId, // Pass jobId as parameter
              {
                onInProgress: () => {},
                onCompleted: (result) => {
                  resolve(result);
                },
                onFailed: (errorMessage) => {
                  reject(new Error(errorMessage));
                },
                onStatusUpdate: (response) => {
                  // Capture the full polling response
                  fullPollingResponse = response;
                },
                onStop: (stopFn: () => void) => {
                  stopPollingFn = stopFn;
                }
              },
              '/job-status/result',
              1000
            );
          });
        };

        // Step 4: Wait for polling to complete (with race for cancellation support)
        const { result: pollingResult, cancel: pollingCancel } = yield race({
          result: call(pollingPromise),
          cancel: take(CALLS_SUMMARY_DETAILS_CANCEL),
        });

        if (pollingCancel) {
          if (typeof stopPollingFn === 'function') {
            stopPollingFn();
          }
          yield put(summaryDataLoading(false));
          if (key) {
            yield put(callsPagePollingCompleted()); // Decrement polling counter on cancel (bulk flow only)
          }
          return;
        }

        try {

          // Transform polling result to match normal flow structure
          // onCompleted receives: Result object (extracted by CommonBulkPoll)
          // fullPollingResponse has: { Status, StatusCode, Message, Data: { JobId, Status, Message, Result }, TimeStamp }
          // Normal flow returns: { Status, StatusCode, Message, Data: {...}, TimeStamp }

          // Convert Unix timestamp (milliseconds) to ISO string with +00:00 format
          let timestamp = fullPollingResponse?.TimeStamp || new Date().toISOString().replace('Z', '+00:00');
          if (typeof timestamp === 'number') {
            // Convert to ISO string and replace Z with +00:00
            timestamp = new Date(timestamp).toISOString().replace('Z', '+00:00');
          }

          const transformedData = {
            Status: fullPollingResponse?.Status || "OK",
            StatusCode: fullPollingResponse?.StatusCode || 200,
            Message: fullPollingResponse?.Message || "Successfully Data Retrieved",
            TimeStamp: timestamp,
            Data: pollingResult || {}  // pollingResult is already the Result object
          };

          yield put(fetchSummaryDataSuccess({
            initialData: transformedData,
          }));
        } catch (pollingError: any) {
          yield put(fetchSummaryDataSuccess({
            initialData: {},
          }));
        } finally {
          yield put(summaryDataLoading(false));
        }
      } else {
        // No jobId received or job submission failed
        yield put(fetchSummaryDataSuccess({
          initialData: {},
        }));
        yield put(summaryDataLoading(false));
      }
    } else {
      // ============================================================
      // NORMAL FLOW - Response contains actual data (no disturbance)
      // ============================================================
      if (response) {
        yield put(fetchSummaryDataSuccess({
          initialData: response,
        }));
      } else {
        yield put(fetchSummaryDataSuccess({
          initialData: {},
        }));
      }
      yield put(summaryDataLoading(false));
    }

    if (key) {
      yield put(callsPagePollingCompleted()); // Decrement polling counter (bulk flow only)
    }
  } catch (error: any) {
    yield put(fetchSummaryDataSuccess({
      initialData: {},
    }));
    yield put(summaryDataLoading(false));
    if (key) {
      yield put(callsPagePollingFailed(error)); // Decrement polling counter on error (bulk flow only)
    }
  } finally {
    if (yield cancelled()) {
      yield put(summaryDataLoading(false));
      if (key) {
        yield put(callsPagePollingCompleted()); // Decrement on cancellation (bulk flow only)
      }
    }
  }
}

export function* fetchQuickFiltersData(action: any) {
  try {
    const response = yield call(getQuickFiltersData, action?.payload);
    yield put(getQuickFilterDataSuccess(response?.Data));
  } catch (error: any) {
  } finally {
  }
}

export function* fetchCallTypeData(action: any) {
  // Get geoCriteria from action payload (passed from Main.tsx)
  // Geo flow uses the normal API; only high-volume/contract flows poll bulk jobs.
  const geoCriteria = action?.geoCriteria;
  const key = isHighVolumeTinFlow() || isContractBulkFlow();
  let stopPollingFn: (() => void) | undefined;

  try {
    if (key) {
      yield put(callsPagePollingStarted()); // Increment polling counter only for bulk flow
    }
    yield put(callTypeDataLoading(true));

    // Step 1: Call getCallTypeData with geoCriteria (with race for cancellation support)
    // - If geoCriteria is null: Normal flow → Standard API → Immediate response
    // - If geoCriteria is not null: Polling flow → Bulk API → Job submission
    const { result: response, cancel } = yield race({
      result: call(getCallTypeData, action?.payload, geoCriteria),
      cancel: take(CALLS_CALLTYPE_DETAILS_CANCEL),
    });

    if (cancel) {
      yield put(callTypeDataLoading(false));
      if (key) {
        yield put(callsPagePollingCompleted()); // Decrement polling counter on cancel (bulk flow only)
      }
      return;
    }

    // Check the actual transport flow. Geo criteria alone does not mean polling.
    if (key && response?.Data?.JobId && (
      response?.Data?.Status === "ACCEPTED"
    )) {
      // ============================================================
      // POLLING FLOW - Response contains jobId for polling
      // ============================================================

      // Step 2: Extract jobId from job submission response
      const jobId = response?.Data?.JobId;
      const isJobSubmitted = response?.Data?.Status === "ACCEPTED" ||
                            response?.Data?.Message === "Job submitted successfully";

      if (jobId && isJobSubmitted) {
        // Step 3: Start polling with the jobId - Poll GET /job-status/result/{jobId}
        let fullPollingResponseCallType: any = null;
        const pollingPromise = () => {
          return new Promise((resolve, reject) => {
            startJobPolling(
              jobId, // Pass jobId as parameter
              {
                onInProgress: () => {},
                onCompleted: (result) => {
                  resolve(result);
                },
                onFailed: (errorMessage) => {
                  reject(new Error(errorMessage));
                },
                onStatusUpdate: (response) => {
                  // Capture the full polling response
                  fullPollingResponseCallType = response;
                },
                onStop: (stopFn: () => void) => {
                  stopPollingFn = stopFn;
                }
              },
              '/job-status/result',
              1000
            );
          });
        };

        // Step 4: Wait for polling to complete (with race for cancellation support)
        const { result: pollingResult, cancel: pollingCancel } = yield race({
          result: call(pollingPromise),
          cancel: take(CALLS_CALLTYPE_DETAILS_CANCEL),
        });

        if (pollingCancel) {
          if (typeof stopPollingFn === 'function') {
            stopPollingFn();
          }
          yield put(callTypeDataLoading(false));
          if (key) {
            yield put(callsPagePollingCompleted()); // Decrement polling counter on cancel (bulk flow only)
          }
          return;
        }

        try {

          // Transform polling result to match normal flow structure
          // onCompleted receives: Result object (extracted by CommonBulkPoll)
          // For CallType, we dispatch only the Data portion (matching normal flow)
          const transformedData = pollingResult || {};

          yield put(fetchCallTypeDataSuccess(transformedData));
        } catch (pollingError: any) {
          yield put(fetchCallTypeDataSuccess({}));
        } finally {
          yield put(callTypeDataLoading(false));
        }
      } else {
        // No jobId received or job submission failed
        yield put(fetchCallTypeDataSuccess({}));
        yield put(callTypeDataLoading(false));
      }
    } else {
      // ============================================================
      // NORMAL FLOW - Response contains actual data (no disturbance)
      // ============================================================
      if (response && response.Data != null) {
        yield put(fetchCallTypeDataSuccess(response.Data));
      } else {
        yield put(fetchCallTypeDataSuccess({}));
      }
      yield put(callTypeDataLoading(false));
    }

    if (key) {
      yield put(callsPagePollingCompleted()); // Decrement polling counter (bulk flow only)
    }
  } catch (error: any) {
    yield put(fetchCallTypeDataSuccess({}));
    yield put(callTypeDataLoading(false));
    if (key) {
      yield put(callsPagePollingFailed(error)); // Decrement polling counter on error (bulk flow only)
    }
  } finally {
    if (yield cancelled()) {
      yield put(callTypeDataLoading(false));
      if (key) {
        yield put(callsPagePollingCompleted()); // Decrement on cancellation (bulk flow only)
      }
    }
  }
}

export function* fetchCallMonthData(action: any) {
  // Get geoCriteria from action payload (passed from MainConnect.tsx)
  // Geo flow uses the normal API; only high-volume/contract flows poll bulk jobs.
  const geoCriteria = action?.geoCriteria;
  const key = isHighVolumeTinFlow() || isContractBulkFlow();
  const stopPollingFns: Array<(() => void) | undefined> = [];

  try {
    const payloadArray = action?.payload || [];
    if (key) {
      yield put(callsPagePollingStarted()); // Increment polling counter only for bulk flow
    }
    yield put(callMonthDataLoading(true));

    // Check the actual transport flow. Geo criteria alone does not mean polling.
    if (key) {
      // ============================================================
      // POLLING FLOW - Submit jobs and poll for results
      // ============================================================

      // Step 1: Submit all jobs and get jobIds (with race for cancellation support)
      const { result: jobResponses, cancel: submitCancel } = yield race({
        result: all(
          payloadArray.map((payload: any) => call(getCallMonthData, payload, geoCriteria))
        ),
        cancel: take(CALLS_CALLMONTH_DETAILS_CANCEL),
      });

      if (submitCancel) {
        yield put(callMonthDataLoading(false));
        if (key) {
          yield put(callsPagePollingCompleted()); // Decrement polling counter on cancel (bulk flow only)
        }
        return;
      }

      // Step 2: Extract jobIds and filter valid submissions
      const jobInfos = jobResponses
        .map((response: any, index: number) => ({
          jobId: response?.Data?.JobId,
          isJobSubmitted: response?.Data?.Status === "ACCEPTED" ||
                         response?.Data?.Message === "Job submitted successfully",
          index: index
        }))
        .filter((info: any) => info.jobId && info.isJobSubmitted);

      const hasJobSubmission = key && jobInfos.length > 0;

      if (hasJobSubmission) {
        // Step 3: Start polling for all jobs
        const pollingPromises = jobInfos.map((jobInfo: any, promiseIndex: number) => {
          let fullPollingResponse: any = null;
          return new Promise((resolve, reject) => {
            startJobPolling(
              jobInfo.jobId,
              {
                onInProgress: () => {},
                onCompleted: (result) => {
                  resolve({
                    result,
                    fullResponse: fullPollingResponse,
                    index: jobInfo.index
                  });
                },
                onFailed: (errorMessage) => {
                  reject(new Error(errorMessage));
                },
                onStatusUpdate: (response) => {
                  fullPollingResponse = response;
                },
                onStop: (stopFn: () => void) => {
                  stopPollingFns[promiseIndex] = stopFn;
                }
              },
              '/job-status/result',
              1000
            );
          });
        });

        // Step 4: Wait for all polling to complete (with race for cancellation support)
        const { result: pollingResults, cancel: pollingCancel } = yield race({
          result: all(
            pollingPromises.map((promise: any) => call(() => promise))
          ),
          cancel: take(CALLS_CALLMONTH_DETAILS_CANCEL),
        });

        if (pollingCancel) {
          stopPollingFns.forEach(stopFn => {
            if (typeof stopFn === 'function') {
              stopFn();
            }
          });
          yield put(callMonthDataLoading(false));
          if (key) {
            yield put(callsPagePollingCompleted()); // Decrement polling counter on cancel (bulk flow only)
          }
          return;
        }

        try {

          // Step 5: Transform polling results to match normal flow structure
          // Normal flow returns an array of: { Status, StatusCode, Message, Data: {...}, TimeStamp }
          const transformedResponses = pollingResults.map((pollingData: any) => {
            // Convert Unix timestamp (milliseconds) to ISO string with +00:00 format
            let timestamp = pollingData.fullResponse?.TimeStamp || new Date().toISOString().replace('Z', '+00:00');
            if (typeof timestamp === 'number') {
              timestamp = new Date(timestamp).toISOString().replace('Z', '+00:00');
            }

            return {
              Status: pollingData.fullResponse?.Status || "OK",
              StatusCode: pollingData.fullResponse?.StatusCode || 200,
              Message: pollingData.fullResponse?.Message || "Successfully Data Retrieved",
              TimeStamp: timestamp,
              Data: pollingData.result || {}  // pollingResult is already the Result object
            };
          });

          yield put(fetchCallMonthDataSuccess(transformedResponses));
        } catch (pollingError: any) {
          // If polling fails, return empty array
          yield put(fetchCallMonthDataSuccess([]));
        } finally {
          yield put(callMonthDataLoading(false));
        }
      } else {
        // No valid jobs submitted
        yield put(fetchCallMonthDataSuccess(jobResponses || []));
        yield put(callMonthDataLoading(false));
      }
    } else {
      // ============================================================
      // NORMAL FLOW - Make API calls for each payload (no disturbance)
      // ============================================================
      const responses = yield all(
        payloadArray.map((payload: any) => call(getCallMonthData, payload))
      );

      yield put(fetchCallMonthDataSuccess(responses));
      yield put(callMonthDataLoading(false));
    }

    if (key) {
      yield put(callsPagePollingCompleted()); // Decrement polling counter (bulk flow only)
    }
  } catch (error: any) {
    yield put(fetchCallMonthDataSuccess([]));
    yield put(callMonthDataLoading(false));
    if (key) {
      yield put(callsPagePollingFailed(error)); // Decrement polling counter on error (bulk flow only)
    }
  } finally {
    if (yield cancelled()) {
      yield put(callMonthDataLoading(false));
      if (key) {
        yield put(callsPagePollingCompleted()); // Decrement on cancellation (bulk flow only)
      }
    }
  }
}

export function* fetchCallsGeoTinCountData(action: any) {
  try {
    yield put(fetchCallsGeoTinCountDataLoading(true));
    const response = yield call(getCallsGeoTinCountData, action?.payload);
    yield put(fetchCallsGeoTinCountDataSuccess(response));
  } catch (error: any) {
    yield put(fetchCallsGeoTinCountDataSuccess({}));
  } finally {
    yield put(fetchCallsGeoTinCountDataLoading(false));
  }
}

export function* callPageSaga() {
  yield takeLatest(SUMMARY_DATA_REQUEST, fetchSummaryData);
  yield takeLatest(CALLSPAGE_QUICKFILTER_DATA_REQUEST, fetchQuickFiltersData);
  yield takeLatest(CALLTYPE_DATA_REQUEST, fetchCallTypeData);
  yield takeLatest(CALLMONTH_DATA_REQUEST, fetchCallMonthData);
  yield takeLatest(CALLS_GEO_TIN_COUNT_DATA_REQUEST, fetchCallsGeoTinCountData);
}